import React, { useState } from 'react';
import { Card, Badge, Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { getAvatarInitials } from '../../utils/employeeName';

const safeFormat = (dateStr, fmt) => {
  try {
    if (!dateStr) return '--';
    return format(new Date(dateStr), fmt);
  } catch {
    return '--';
  }
};

const safeDate = (dateStr) => {
  try {
    return dateStr ? new Date(dateStr) : null;
  } catch {
    return null;
  }
};

const STATUS_CONFIG = {
  COMPLETED: { label: 'Hoàn thành', variant: 'success' },
  COMPLETED_WAIT_APPROVE: { label: 'Chờ duyệt', variant: 'warning' },
  IN_PROGRESS: { label: 'Đang thực hiện', variant: 'primary' },
  PENDING: { label: 'Chờ xử lý', variant: 'warning' },
  APPROVED: { label: 'Đã duyệt', variant: 'info' },
  // #190609 — bot chỉ còn ĐỀ XUẤT; APPROVE_BOT là dữ liệu cũ của cơ chế bot tự duyệt đã bỏ.
  PROPOSED_BOT: { label: 'Bot đề xuất', variant: 'warning' },
  APPROVE_BOT: { label: 'Đã duyệt (bot cũ)', variant: 'success' },
  CANCELLED: { label: 'Đã hủy', variant: 'danger' },
  REJECTED: { label: 'Từ chối', variant: 'danger' },
  ABSENT: { label: 'Vắng mặt', variant: 'dark' },
};

const ShiftHeader = ({ shift, taskSummary, orderSummary, voucherSummary, reconciliation }) => {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (!shift) return null;

  const { employee, branch, template, attendance, status, start_time, end_time } = shift;
  const employeeName = employee?.full_name || 'Nhân viên';
  const branchName = branch?.name || 'Chi nhánh';
  const templateName = template?.name || 'Ca tùy chỉnh';

  const statusCfg = STATUS_CONFIG[status] || { label: status, variant: 'secondary' };

  const checkIn = safeDate(attendance?.check_in_time);
  const checkOut = safeDate(attendance?.check_out_time);
  const plannedStart = safeDate(start_time);
  const plannedEnd = safeDate(end_time);

  const isLate = checkIn && plannedStart && checkIn > plannedStart;
  const lateMinutes = isLate ? Math.round((checkIn - plannedStart) / 60000) : 0;
  const isEarlyLeave = checkOut && plannedEnd && checkOut < plannedEnd;
  const isOvertime = checkOut && plannedEnd && checkOut > plannedEnd;

  const actualHours = attendance?.actual_hours;
  const formattedHours = actualHours
    ? `${Math.floor(actualHours)}h${String(Math.round((actualHours % 1) * 60)).padStart(2, '0')}m`
    : '--';

  const difference = reconciliation?.difference ?? 0;
  const hasDiscrepancy = Math.abs(difference) >= 1000;

  return (
    <>
      <Card className='mb-4 shadow-sm border-0'>
        <Card.Body>
          {/* Top Row: Employee Info + Badges + Status */}
          <div className='d-flex justify-content-between align-items-center flex-wrap mb-3'>
            <div className='d-flex align-items-center'>
              <div
                className='me-3 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold'
                style={{ width: 48, height: 48, fontSize: '1.1rem' }}
              >
                {getAvatarInitials(employeeName)}
              </div>
              <div>
                <h5 className='mb-1 fw-bold'>{employeeName}</h5>
                <div className='text-muted small'>
                  {branchName} &bull; {templateName} &bull; {safeFormat(start_time, 'dd/MM/yyyy')}
                </div>
              </div>
            </div>

            <div className='d-flex align-items-center gap-2'>
              <Badge bg='info' className='text-uppercase'>{shift.shift_type || 'NORMAL'}</Badge>
              {shift.is_cashier && <Badge bg='secondary'>Thu ngân</Badge>}
              {shift.is_qc && <Badge bg='secondary'>QC</Badge>}
              {shift.is_overtime && <Badge bg='warning' text='dark'>Tăng ca</Badge>}
              <Badge bg={statusCfg.variant} className='ms-1'>{statusCfg.label}</Badge>
            </div>
          </div>

          <hr className='my-3' />

          {/* Time Row */}
          <div className='d-flex flex-wrap gap-4 align-items-center mb-3'>
            <div className='d-flex align-items-center gap-2'>
              <i className='icofont-clock-time text-muted'></i>
              <span className='text-muted small'>Kế hoạch:</span>
              <span className='fw-medium'>{safeFormat(start_time, 'HH:mm')} - {safeFormat(end_time, 'HH:mm')}</span>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <i className='icofont-clock-time text-success'></i>
              <span className='text-muted small'>Thực tế:</span>
              <span className='fw-medium text-success'>
                {safeFormat(attendance?.check_in_time, 'HH:mm')} - {safeFormat(attendance?.check_out_time, 'HH:mm')}
              </span>
            </div>

            {isLate && (
              <Badge bg='warning' text='dark'>
                <i className='icofont-warning me-1'></i>Đi muộn {lateMinutes}p
              </Badge>
            )}
            {isEarlyLeave && <Badge bg='warning' text='dark'>Về sớm</Badge>}
            {isOvertime && <Badge bg='info'>Tăng ca</Badge>}

            <Badge bg='primary'>
              <i className='icofont-clock-time me-1'></i>Tổng: {formattedHours}
            </Badge>
          </div>

          {/* Approval/Completion Info */}
          {(shift.completed_at || shift.approved_at) && (
            <div className='d-flex flex-wrap gap-4 align-items-center mb-3'>
              {shift.completed_at && (
                <div className='d-flex align-items-center gap-2'>
                  <i className='icofont-check text-success'></i>
                  <span className='text-muted small'>Hoàn thành lúc:</span>
                  <span className='small fw-medium'>{safeFormat(shift.completed_at, 'dd/MM HH:mm')}</span>
                </div>
              )}
              {shift.approved_at && (
                <div className='d-flex align-items-center gap-2'>
                  <i className='icofont-check-circled text-info'></i>
                  <span className='text-muted small'>Duyệt lúc:</span>
                  <span className='small fw-medium'>{safeFormat(shift.approved_at, 'dd/MM HH:mm')}</span>
                  {shift.approved_by && (
                    <span className='small text-muted'>bởi {typeof shift.approved_by === 'object' ? shift.approved_by.full_name : shift.approved_by}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Evidence Row */}
          {(attendance?.check_in_evidence_url || attendance?.check_out_evidence_url) && (
            <div className='d-flex align-items-center gap-3 mb-3'>
              <span className='text-muted small'>Bằng chứng:</span>
              {attendance.check_in_evidence_url && (
                <div className='text-center'>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => setLightboxUrl(attendance.check_in_evidence_url)}
                  >
                    <img
                      src={attendance.check_in_evidence_url}
                      alt='Check-in'
                      className='rounded border'
                      style={{ width: 100, height: 75, objectFit: 'cover' }}
                    />
                  </div>
                  <div className='x-small text-muted mt-1'>Check-in</div>
                </div>
              )}
              {attendance.check_out_evidence_url && (
                <div className='text-center'>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => setLightboxUrl(attendance.check_out_evidence_url)}
                  >
                    <img
                      src={attendance.check_out_evidence_url}
                      alt='Check-out'
                      className='rounded border'
                      style={{ width: 100, height: 75, objectFit: 'cover' }}
                    />
                  </div>
                  <div className='x-small text-muted mt-1'>Check-out</div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Mini Summary Cards */}
      <div className='row g-3 mb-4'>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-success bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 40, height: 40 }}>
                <i className='icofont-tasks-alt text-success fs-5'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>
                  {taskSummary?.completed || 0}/{taskSummary?.total || 0}
                </div>
                <div className='text-muted x-small'>Tasks hoàn thành</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-primary bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 40, height: 40 }}>
                <i className='icofont-cart text-primary fs-5'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{orderSummary?.total_orders || 0}</div>
                <div className='text-muted x-small'>
                  {new Intl.NumberFormat('vi-VN').format(orderSummary?.total_revenue || 0)}đ
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-danger bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 40, height: 40 }}>
                <i className='icofont-bill text-danger fs-5'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{voucherSummary?.total_vouchers || 0}</div>
                <div className='text-muted x-small'>
                  {new Intl.NumberFormat('vi-VN').format(voucherSummary?.total_amount || 0)}đ chi
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
        {shift.is_cashier && (
          <div className='col-6 col-md-3'>
            <Card className={`shadow-sm border-0 h-100 ${hasDiscrepancy ? 'border-danger' : ''}`}>
              <Card.Body className='d-flex align-items-center gap-3'>
                <div className={`rounded ${hasDiscrepancy ? 'bg-danger' : 'bg-info'} bg-opacity-10 p-2 d-flex align-items-center justify-content-center`} style={{ width: 40, height: 40 }}>
                  <i className={`icofont-money ${hasDiscrepancy ? 'text-danger' : 'text-info'} fs-5`}></i>
                </div>
                <div>
                  <div className={`fw-bold fs-5 ${difference > 0 ? 'text-success' : difference < 0 ? 'text-danger' : ''}`}>
                    {difference > 0 ? '+' : ''}{new Intl.NumberFormat('vi-VN').format(difference)}đ
                  </div>
                  <div className='text-muted x-small'>Chênh lệch tiền mặt</div>
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='small'>Bằng chứng</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center p-2'>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt='Evidence'
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ShiftHeader;
